// US-SETUP-03 -- change the guardian relationship type for a child.
//
// Presents the canonical guardian-type list in a dropdown and calls
// child_guardian_type_update on save. Optimistic: doesn't fetch the
// *current* type, just takes the new value.

import { type AppError, childGuardianTypeUpdate, guardianTypesList } from '@chorepop/core/client';
import { type Component, For, Show, createResource, createSignal } from 'solid-js';

type Props = {
  /** If null, dialog is hidden. Otherwise the child being edited. */
  when: { childAccountId: number; childDisplayName: string } | null;
  sessionId: string;
  onSaved: (childDisplayName: string, newTypeLabel: string) => void;
  onCancel: () => void;
};

export const EditGuardianTypeDialog: Component<Props> = (props) => {
  const [types] = createResource(async () => {
    const result = await guardianTypesList();
    if (!result.ok) throw new Error(result.error.message);
    return result.value;
  });
  const [selected, setSelected] = createSignal('parent');
  const [saving, setSaving] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const save = async () => {
    const target = props.when;
    if (!target) return;
    setSaving(true);
    setErrorMessage(null);
    try {
      const result = await childGuardianTypeUpdate({
        sessionId: props.sessionId,
        childAccountId: target.childAccountId,
        guardianTypeCode: selected(),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      const picked = types()?.find((t) => t.typeCode === selected());
      props.onSaved(target.childDisplayName, picked?.typeLabel ?? selected());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Show when={props.when}>
      {(target) => (
        <div
          class="confirm-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving()) props.onCancel();
          }}
        >
          <div class="confirm-dialog" role="dialog" aria-modal="true">
            <h2>Change relationship with {target().childDisplayName}</h2>
            <p>
              How are you related to {target().childDisplayName}? This is used to label the
              relationship in family activity and history.
            </p>

            <Show when={types()} fallback={<p>Loading options…</p>}>
              {(opts) => (
                <label class="field">
                  <span class="field-label">Relationship</span>
                  <select
                    value={selected()}
                    disabled={saving()}
                    onChange={(e) => setSelected(e.currentTarget.value)}
                  >
                    <For each={opts()}>
                      {(t) => <option value={t.typeCode}>{t.typeLabel}</option>}
                    </For>
                  </select>
                </label>
              )}
            </Show>

            <Show when={errorMessage()}>
              {(msg) => (
                <div class="form-error" role="alert">
                  {msg()}
                </div>
              )}
            </Show>

            <div class="confirm-buttons">
              <button
                type="button"
                class="ghost"
                onClick={() => props.onCancel()}
                disabled={saving()}
              >
                Cancel
              </button>
              <button type="button" class="primary" onClick={save} disabled={saving() || !types()}>
                {saving() ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Show>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired or you are not allowed to change this.';
    case 'not_found':
      return 'That child account no longer exists.';
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
